// src/pages/ApplicationChampionship/components/ChampionshipTable/ChampionshipTable.tsx
import { Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text, Badge } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Championship } from '@/model';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';

export interface ChampionshipTableProps {
  data: Championship[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onEditClick: (item: Championship) => void;
  onDeleteClick: (item: Championship) => void;
}

export const ChampionshipTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  totalItems,
  sorting,
  onSortingChange,
  onPaginationChange,
  onEditClick,
  onDeleteClick,
}: ChampionshipTableProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<Championship>();

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.application_championship.components.championship_table.championship_table_created_at'),
    }),

    columnHelper.accessor('name', {
      cell: props => <Text fontWeight='medium'>{props.getValue()}</Text>,
      header: t('pages.application_championship.components.championship_table.championship_table_name'),
    }),

    columnHelper.accessor('is_active', {
      cell: props => {
        const value = props.getValue();
        return (
          <Badge colorScheme={value ? 'green' : 'red'}>
            {value
              ? t('pages.application_championship.components.championship_table.championship_table_is_active_yes')
              : t('pages.application_championship.components.championship_table.championship_table_is_active_no')}
          </Badge>
        );
      },
      header: t('pages.application_championship.components.championship_table.championship_table_is_active'),
    }),

    columnHelper.accessor('is_filtered', {
      cell: props => {
        const value = props.getValue();
        return (
          <Badge colorScheme={value ? 'blue' : 'gray'}>
            {value
              ? t('pages.application_championship.components.championship_table.championship_table_is_filtered_yes')
              : t('pages.application_championship.components.championship_table.championship_table_is_filtered_no')}
          </Badge>
        );
      },
      header: t('pages.application_championship.components.championship_table.championship_table_is_filtered'),
    }),

    columnHelper.display({
      id: 'actions',
      header: t('pages.application_championship.components.championship_table.championship_table_actions'),
      cell: props => (
        <Flex justifyContent='end'>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.application_championship.components.championship_table.championship_table_show_more_actions')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onEditClick?.(props.row.original as Championship)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' />
                  </Text>
                }
              >
                <Text>{t('pages.application_championship.components.championship_table.championship_table_view_details')}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick?.(props.row.original as Championship)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_championship.components.championship_table.championship_table_delete')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={columns}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
